import React, { useState, useEffect } from 'react';
import { Plus, Pencil, X, Trash2, Lock, ImagePlus } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import ImageField from '../../components/ImageField';
import MediaPicker from '../../components/MediaPicker';
import { requestPin } from '../../components/PinModal';
import { toast } from 'sonner';

const EMPTY = {
  name: '', slug: '', category: '', price: 0, comparePrice: null, stock: 0, active: true,
  saleMode: 'admision', ownerId: 'matriz', image: '', gallery: [], shortDescription: '', description: '', detailHtml: '', features: [], tags: [],
  seo: { metaTitle: '', metaDescription: '' }, faq: [],
};

export default function AdminProductos() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [galleryPicker, setGalleryPicker] = useState(false);

  const confirmDelete = async () => {
    const token = await requestPin(`Eliminar producto "${deleting.name}"`);
    if (!token) return;
    try {
      await api.deleteProduct(deleting.id, token);
      setProducts((prev) => prev.filter((p) => p.id !== deleting.id));
      toast.success(`"${deleting.name}" eliminado`);
      setDeleting(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  useEffect(() => {
    api.getProducts(true).then(setProducts).catch(() => {});
    api.getValueLists().then((lists) => setCategories(lists.find((l) => l.id === 'categorias')?.values || [])).catch(() => {});
    api.getFranchisesFresh().then(setFranchises).catch(() => {});
  }, []);

  const toggleActive = async (p) => {
    try {
      const updated = await api.updateProduct(p.id, { active: !p.active });
      setProducts((prev) => prev.map((x) => x.id === p.id ? updated : x));
      toast.success(`${p.name} ${updated.active ? 'activado' : 'desactivado'}`);
    } catch { toast.error('Error al actualizar'); }
  };

  // Edición rápida: se actualiza en local y se persiste al salir del campo (onBlur)
  const quickChange = (id, field, value) => {
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, [field]: value } : p));
  };
  const quickSave = async (p, field) => {
    try {
      await api.updateProduct(p.id, { [field]: p[field] });
      toast.success('Guardado');
    } catch { toast.error('Error al guardar'); }
  };

  const saveEditing = async () => {
    if (!editing.name.trim() || !editing.price) { toast.error('Nombre y precio son obligatorios'); return; }
    try {
      if (editing.id) {
        const { id, ...data } = editing;
        const updated = await api.updateProduct(id, data);
        setProducts((prev) => prev.map((p) => p.id === id ? updated : p));
      } else {
        const created = await api.createProduct(editing);
        setProducts((prev) => [created, ...prev]);
      }
      setEditing(null);
      toast.success('Producto guardado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-productos">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-stone-800">Productos</h1>
          <p className="text-sm text-stone-500">Edición rápida de precio y stock directamente en la tabla.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm" data-testid="new-product-button">
          <Plus className="w-4 h-4" /> Nuevo producto
        </button>
      </div>

      {editing && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-8" data-testid="product-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-black text-stone-800">{editing.id ? 'Editar producto' : 'Nuevo producto'}</h2>
            <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-700 transition-colors" data-testid="close-product-form"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Nombre comercial *</label>
              <input value={editing.name} onChange={set('name')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="product-name-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">URL SEO (slug)</label>
              <input value={editing.slug} onChange={set('slug')} placeholder="se genera automáticamente" className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Categoría</label>
              <select value={editing.category} onChange={set('category')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="product-category-select">
                <option value="">Selecciona…</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Propietario</label>
              <select value={editing.ownerId} onChange={set('ownerId')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
                {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-stone-500 mb-1">Precio € *</label>
                <input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="product-price-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 mb-1">Precio ant.</label>
                <input type="number" step="0.01" value={editing.comparePrice || ''} onChange={(e) => setEditing({ ...editing, comparePrice: e.target.value ? parseFloat(e.target.value) : null })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 mb-1">Stock</label>
                <input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: parseInt(e.target.value) || 0 })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="product-stock-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Modo de venta</label>
              <select value={editing.saleMode || 'admision'} onChange={set('saleMode')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="product-salemode-select">
                <option value="admision">Solicitud de admisión (por defecto)</option>
                <option value="libre">Venta libre</option>
              </select>
              <p className="text-[10px] text-stone-400 mt-1">Venta libre solo para productos sin limitación operativa (online, tarjetas regalo, digitales).</p>
            </div>
            <ImageField label="Imagen principal" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} ownerId={editing.ownerId} testid="pick-product-image" />
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 mb-1">Galería de imágenes (se muestra debajo de la foto principal)</label>
              <div className="flex flex-wrap items-center gap-3">
                {(editing.gallery || []).map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={url} alt="" className="w-16 h-12 object-cover rounded border border-stone-200 bg-stone-50" />
                    <button type="button" onClick={() => setEditing({ ...editing, gallery: editing.gallery.filter((_, x) => x !== i) })} className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 transition-colors text-white rounded-full w-5 h-5 flex items-center justify-center" data-testid={`gallery-remove-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => setGalleryPicker(true)} className="w-16 h-12 rounded border border-dashed border-stone-300 bg-stone-50 hover:border-amber-400 transition-colors flex items-center justify-center" title="Añadir imagen a la galería" data-testid="gallery-add-button">
                  <ImagePlus className="w-4 h-4 text-stone-400" />
                </button>
              </div>
              <MediaPicker open={galleryPicker} onClose={() => setGalleryPicker(false)} onSelect={(url) => setEditing((prev) => ({ ...prev, gallery: [...(prev.gallery || []), url] }))} ownerId={editing.ownerId} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 mb-1">Descripción corta</label>
              <input value={editing.shortDescription} onChange={set('shortDescription')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 mb-1">Descripción completa</label>
              <textarea value={editing.description} onChange={set('description')} rows={3} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-stone-500 mb-1">Contenido detallado (HTML) — se muestra debajo de la ficha, con títulos, listas e imágenes</label>
              <textarea value={editing.detailHtml || ''} onChange={set('detailHtml')} rows={10} className="w-full border border-stone-300 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="product-detailhtml-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Meta Title (SEO)</label>
              <input value={editing.seo?.metaTitle || ''} onChange={(e) => setEditing({ ...editing, seo: { ...editing.seo, metaTitle: e.target.value } })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Meta Description (SEO)</label>
              <input value={editing.seo?.metaDescription || ''} onChange={(e) => setEditing({ ...editing, seo: { ...editing.seo, metaDescription: e.target.value } })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
            <button onClick={saveEditing} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-product-button">Guardar producto</button>
          </div>
        </div>
      )}

      {deleting && (
        <div className="bg-white rounded-lg border-2 border-red-300 p-5 mb-6 max-w-xl flex flex-wrap items-center gap-3" data-testid="delete-product-confirm">
          <Lock className="w-4 h-4 text-red-400" />
          <p className="text-sm text-stone-700 flex-1 min-w-48">Eliminar <strong>{deleting.name}</strong> definitivamente. Esta acción no se puede deshacer.</p>
          <button onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 transition-colors text-white px-4 py-2 rounded text-sm font-bold" data-testid="delete-product-confirm-btn">Eliminar</button>
          <button onClick={() => setDeleting(null)} className="px-3 py-2 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Producto</th>
              <th className="px-4 py-3 font-medium">Propietario</th>
              <th className="px-4 py-3 font-medium w-28">Precio €</th>
              <th className="px-4 py-3 font-medium w-24">Stock</th>
              <th className="px-4 py-3 font-medium w-20">Activo</th>
              <th className="px-4 py-3 w-14"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-stone-100" data-testid={`product-row-${p.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {p.image ? <img src={p.image} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 rounded bg-stone-100" />}
                    <div>
                      <p className="font-bold text-stone-800">{p.name}</p>
                      <p className="text-xs text-stone-400">/{p.slug}</p>
                      <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${p.saleMode === 'libre' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`salemode-badge-${p.slug}`}>
                        {p.saleMode === 'libre' ? 'Venta libre' : 'Admisión'}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-600 text-xs">{franchises.find((f) => f.id === p.ownerId)?.name || '—'}</td>
                <td className="px-4 py-3">
                  <input
                    type="number" step="0.01" value={p.price}
                    onChange={(e) => quickChange(p.id, 'price', parseFloat(e.target.value) || 0)}
                    onBlur={() => quickSave(p, 'price')}
                    className="w-24 border border-stone-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid={`quick-price-${p.slug}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number" value={p.stock}
                    onChange={(e) => quickChange(p.id, 'stock', parseInt(e.target.value) || 0)}
                    onBlur={() => quickSave(p, 'stock')}
                    className="w-20 border border-stone-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid={`quick-stock-${p.slug}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`toggle-active-${p.slug}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setEditing({ ...p })} className="text-stone-400 hover:text-amber-600 transition-colors" data-testid={`edit-product-${p.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleting(p)} className="text-stone-400 hover:text-red-600 transition-colors" title="Eliminar producto" data-testid={`delete-product-${p.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
